import React, { useEffect, useState } from "react";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Space } from "antd";
import DataTable from "../../components/Comman/CustomTable/CommonTable";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteOrganization,
  OrganizationListAction,
} from "../../actions/admin";
import { useNavigate } from "react-router-dom";
import SideDrawer from "../../components/Comman/Drawer/SideDrawer";
import ConfirmDeleteModal from "../../components/Comman/ConfirmDeleteModal/ConfirmDeleteModal";
import { formatMobileNumber } from "../../utils/comman";

const OrganizationList = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const handleRefresh = useSelector(
    (state: any) => state?.General?.updateListing
  );
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] =
    useState<boolean>(false);
  const [selectedOrganization, setSelectedOrganization] = useState<any>(null);
  const [selectOrganizationId, setSelectOrganizationId] = useState<
    number | null
  >(null);
  const [deleteModelVisible, setDeleteModelVisible] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({
    field: null,
    order: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const dispatch = useDispatch<any>();

  const fetchOrganizationList = (params = {}) => {
    setLoading(true);
    // Always include current sort parameters in the request
    const requestParams = {
      ...params,
    };

    dispatch(
      OrganizationListAction(requestParams, (resp: any) => {
        setLoading(false);
        if (resp?.status === 200) {
          setData(resp.data || []);
          setPagination({
            ...pagination,
            total: resp.pagination?.totalItems,
            current: resp.pagination?.currentPage,
            pageSize: resp.pagination?.pageSize,
          });
        }
      })
    );
  };

  useEffect(() => {
    fetchOrganizationList({
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
    });
  }, [handleRefresh]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let newSortConfig = { ...sortConfig };
    if (sorter && Object.keys(sorter).length > 0) {
      newSortConfig = {
        field: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      };
    }
    setSortConfig(newSortConfig);
    fetchOrganizationList({
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      ...(newSortConfig.field && newSortConfig.order
        ? {
            sort: newSortConfig.field,
            order: newSortConfig.order,
          }
        : {}),
    });
  };

  const handleViewOrganization = (record) => {
    navigate(`/admin/view-organization/${record.id}`);
  };

  const handleDeleteModel = (id?: number) => {
    if (id) setSelectOrganizationId(id); // Store the selected user ID
    setDeleteModelVisible(!deleteModelVisible);
  };

  const handleEditClick = (record: any) => {
    handleIsOrganizationModelOpen();
    setSelectedOrganization(record); // Store the selected organization's data
  };

  const handleIsOrganizationModelOpen: any = () => {
    setIsOrganizationModalOpen(!isOrganizationModalOpen);
  };

  const handleOrganizationDeletion = () => {
    setLoading(true);
    dispatch(
      deleteOrganization(selectOrganizationId, (resp: any) => {
        setLoading(false);
        if (resp?.status === 200) {
          // User deleted successfully, handle UI update if needed
          handleDeleteModel();
        } else {
          handleDeleteModel();
        }
      })
    );
  };

  const columns = [
    {
      title: "Organization",
      dataIndex: "name",
      key: "name",
      render: (name: any) => name,
      sorter: true, // Just set to true since we're handling sorting on the server
    },
    {
      title: "Division of",
      dataIndex: "divisionOf",
      key: "parentOrganization",
      render: (text, record) => {
        return record?.parentOrganization?.name || "-";
      },
      sorter: true,
    },
    {
      title: "Contact Name",
      dataIndex: "primare_contact_name",
      key: "primare_contact_name",
      sorter: true,
    },
    {
      title: "Phone",
      dataIndex: "primare_contact_phone",
      key: "primare_contact_phone",
      render: (data) => formatMobileNumber(data),
    },
    {
      title: "Reporting Only",
      dataIndex: "Organization_IsReporting",
      key: "Organization_IsReporting",
      render: (_, data) =>
        data?.Organization_IsReporting == true
          ? "Yes"
          : data?.Organization_IsReporting == false
          ? "No"
          : "-",
    },
    {
      title: "No. of Users",
      dataIndex: "totalUsersCount",
      key: "totalUsersCount",
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <span
            style={{ color: "#3DA57F", fontWeight: "bold", fontSize: "16px" }}
          >
            <EditOutlined onClick={() => handleEditClick(record)} />
          </span>
          <span
            style={{ color: "#3DA57F", fontWeight: "bold", fontSize: "16px" }}
          >
            <EyeOutlined onClick={() => handleViewOrganization(record)} />
          </span>
          <span
            style={{ color: "#3DA57F", fontWeight: "bold", fontSize: "16px" }}
          >
            <DeleteOutlined onClick={() => handleDeleteModel(record?.id)} />
          </span>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Organizations</h1>
      </div>
      <div className="table-col">
        <DataTable
          columns={columns}
          data={data}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          onTableChange={handleTableChange}
        />
      </div>

      <SideDrawer
        open={isOrganizationModalOpen}
        onClose={handleIsOrganizationModelOpen}
        type={"organization"}
        organizationData={selectedOrganization} // Pass only selected record
      />

      {deleteModelVisible && (
        <ConfirmDeleteModal
          handleConfirmDeleteModel={handleDeleteModel}
          type="delete"
          handleModelFunction={handleOrganizationDeletion}
        />
      )}
    </div>
  );
};

export default OrganizationList;
